// services/tradesService.ts
import { apiService } from './api';
export type SellerRank = 'A' | 'B' | 'C' | 'D' | 'N';

export interface ProductCategory {
  category_id: number; category_name: string; description: string | null;
  is_active: boolean; product_count: number;
  created_at: string; created_by: string; updated_at: string | null; updated_by: string | null;
}

export interface ProductCategoryPayload { 
  category_name: string; description?: string; 
  is_active?: boolean; 
}

export interface Product {
  product_id: number; product_name: string; category: number | null;
  category_detail: { category_id: number; category_name: string } | null;
  unit_of_measure: string; litre_per_unit: string | null;  
  is_child_product: boolean; parent_product_id: number | null;
  parent_product_id_name: string | null; parent_product_quantity: number | null;
  description: string | null; is_active: boolean;
  created_at: string; created_by: string; updated_at: string | null; updated_by: string | null;
}

export interface ProductPayload {
  product_name: string; category?: number | null; unit_of_measure?: string; litre_per_unit?: number | null;     
  is_child_product?: boolean; parent_product_id?: number | null;
  parent_product_quantity?: number | null; description?: string; is_active?: boolean;
}

export interface SellerInfo {
  seller_info_id: number; seller_type: string; seller_name: string;
  email: string | null; mobile: string; seller_rank: SellerRank;   
  alternative_mobile: string | null; address: string | null;
  created_at: string; created_by: string; updated_at: string | null; updated_by: string | null;
}
export interface SellerInfoPayload {
  seller_type: string; seller_name: string; email?: string; mobile: string;
  alternative_mobile?: string; address?: string; seller_rank: SellerRank;   
}

export interface CostCategory {
  cost_category_id: number; cost_category_name: string; description: string | null;
  is_active: boolean; created_at: string; created_by: string;
  updated_at: string | null; updated_by: string | null;
}
export interface CostCategoryPayload { cost_category_name: string; description?: string; is_active?: boolean; }

// ── ROI Info ──────────────────────────────────────────────────────────────────
export interface RoiInfo {
  roi_info_id:          number;
  investor_name:        string;
  invest_return_date:   string;
  roi_type_name:        'Invest' | 'Return';
  invest_return_amount: string;   // Decimal as string from DRF
  remarks:              string | null;
  created_at:           string;
  created_by:           string;
  updated_at:           string | null;
  updated_by:           string | null;
}
 
export interface RoiInfoPayload {
  investor_name:        string;
  invest_return_date:   string;
  roi_type_name:        'Invest' | 'Return';
  invest_return_amount: number;
  remarks?:             string;
}

export interface ProductMinimal { product_id: number; product_name: string; unit_of_measure: string; }
export interface SellerMinimal  { seller_info_id: number; seller_name: string; seller_type: string; mobile: string; }

export interface CostCategoryMinimal { cost_category_id: number; cost_category_name: string; }

export interface CostInfo {
  cost_info_id: number; cost_date: string;
  cost_category: number | null; cost_category_detail: CostCategoryMinimal | null;
  cost_name: string; cost_amount: string; remarks: string | null;
  created_at: string; created_by: string; updated_at: string | null; updated_by: string | null;
}
export interface CostInfoPayload {
  cost_date: string; cost_category?: number | null;
  cost_name: string; cost_amount: number; remarks?: string;
}

export interface PurchaseInfo {
  purchase_info_id: number; purchase_date: string;
  product: number; product_detail: ProductMinimal | null;
  quantity_pcs: number; purchase_rate: string; total_purchase_price: string;
  remarks: string | null;
  created_at: string; created_by: string; updated_at: string | null; updated_by: string | null;
}
export interface PurchaseInfoPayload {
  purchase_date: string; product: number; quantity_pcs: number;
  purchase_rate: number; total_purchase_price?: number; remarks?: string;
}

// ── Purchase Price List ───────────────────────────────────────────────────────
export interface PurchasePriceList {
  purchase_price_id:  number;
  product:            number;
  product_name:       string;
  purchase_rate:      string;   // Decimal as string from DRF
  purchase_quantity:  number;
  sell_quantity:      number;
  remaining_quantity: number;   // GENERATED column = purchase_quantity - sell_quantity
  sell_rate:          string;
  remarks:            string | null;
  created_at:         string; created_by: string;
  updated_at:         string | null; updated_by: string | null;
}

// Minimal nested shape returned inside SellingInfo / ReturnInfo responses
export interface PurchasePriceDetail {
  purchase_price_id:  number;
  purchase_rate:      string;
  remaining_quantity: number;
}

// ── Selling Info ──────────────────────────────────────────────────────────────
export interface SellingInfo {
  selling_info_id: number; sale_date: string;
  seller_info: number | null; seller_detail: SellerMinimal | null;
  product: number; product_detail: ProductMinimal | null;
  purchase_price: number | null;
  purchase_price_detail: PurchasePriceDetail | null;
  quantity_pcs: number; sell_rate: string;
  discount_amount: string;           // ← NEW
  total_sales_price: string;
  cash_amount: string; due_amount: string; sell_type: string;
  remarks: string | null;
  created_at: string; created_by: string; updated_at: string | null; updated_by: string | null;
}
export interface SellingInfoPayload {
  sale_date: string; seller_info?: number | null; product: number;
  purchase_price?: number | null;
  quantity_pcs: number; sell_rate: number;
  discount_amount?: number;          // ← NEW (optional, default 0)
  total_sales_price?: number;
  cash_amount: number; due_amount?: number; sell_type: string; remarks?: string;
}

export type SellingBulkItemPayload = Omit<SellingInfoPayload, 'seller_info'>;
export interface SellingBulkPayload { seller_info: number; items: SellingBulkItemPayload[]; }

// ── Return Info ───────────────────────────────────────────────────────────────
export interface ReturnInfo {
  return_info_id: number; return_date: string;
  seller_info: number | null; seller_detail: SellerMinimal | null;
  product: number; product_detail: ProductMinimal | null;
  purchase_price: number | null;
  purchase_price_detail: PurchasePriceDetail | null;
  quantity_pcs: number; sell_rate: string;
  discount_amount: string;           // ← NEW
  total_sales_price: string;
  cash_amount: string; due_amount: string; return_type: string;
  remarks: string | null;
  created_at: string; created_by: string; updated_at: string | null; updated_by: string | null;
}
export interface ReturnInfoPayload {
  return_date: string; seller_info?: number | null; product: number;
  purchase_price?: number | null;
  quantity_pcs: number; sell_rate: number;
  discount_amount?: number;          // ← NEW (optional, default 0)
  total_sales_price?: number;
  cash_amount: number; due_amount?: number; return_type: string; remarks?: string;
}

export interface ListResponse<T> {
  success: boolean; results: T[]; count: number;
  totals?: Record<string, number | null>;
}
export interface DetailResponse<T> { success: boolean; data: T; message?: string; }

class TradesService {
  // ── Categories ──────────────────────────────────────────────────────────────
  listCategories(params?: { is_active?: boolean; search?: string }) { return apiService.get<ListResponse<ProductCategory>>('/trades/categories/', { params }); }
  getCategory(id: number)                      { return apiService.get<DetailResponse<ProductCategory>>(`/trades/categories/${id}/`); }
  createCategory(data: ProductCategoryPayload) { return apiService.post<DetailResponse<ProductCategory>>('/trades/categories/', data); }
  updateCategory(id: number, data: Partial<ProductCategoryPayload>) { return apiService.put<DetailResponse<ProductCategory>>(`/trades/categories/${id}/`, data); }
  deleteCategory(id: number)                   { return apiService.delete<DetailResponse<null>>(`/trades/categories/${id}/`); }

  // ── Products ────────────────────────────────────────────────────────────────
  listProducts(params?: { is_active?: boolean; search?: string; category_id?: number }) { return apiService.get<ListResponse<Product>>('/trades/products/', { params }); }
  getProduct(id: number)                   { return apiService.get<DetailResponse<Product>>(`/trades/products/${id}/`); }
  createProduct(data: ProductPayload)      { return apiService.post<DetailResponse<Product>>('/trades/products/', data); }
  updateProduct(id: number, data: Partial<ProductPayload>) { return apiService.put<DetailResponse<Product>>(`/trades/products/${id}/`, data); }
  deleteProduct(id: number)                { return apiService.delete<DetailResponse<null>>(`/trades/products/${id}/`); }

  // ── Sellers ─────────────────────────────────────────────────────────────────
  listSellers(params?: { search?: string; seller_type?: string }) { return apiService.get<ListResponse<SellerInfo>>('/trades/sellers/', { params }); }
  getSeller(id: number)                      { return apiService.get<DetailResponse<SellerInfo>>(`/trades/sellers/${id}/`); }
  createSeller(data: SellerInfoPayload)      { return apiService.post<DetailResponse<SellerInfo>>('/trades/sellers/', data); }
  updateSeller(id: number, data: Partial<SellerInfoPayload>) { return apiService.put<DetailResponse<SellerInfo>>(`/trades/sellers/${id}/`, data); }
  deleteSeller(id: number)                   { return apiService.delete<DetailResponse<null>>(`/trades/sellers/${id}/`); }

  // ── Cost Categories ──────────────────────────────────────────────────────────
  listCostCategories(params?: { is_active?: boolean; search?: string }) { return apiService.get<ListResponse<CostCategory>>('/trades/cost-categories/', { params }); }
  getCostCategory(id: number)                      { return apiService.get<DetailResponse<CostCategory>>(`/trades/cost-categories/${id}/`); }
  createCostCategory(data: CostCategoryPayload) { return apiService.post<DetailResponse<CostCategory>>('/trades/cost-categories/', data); }
  updateCostCategory(id: number, data: Partial<CostCategoryPayload>) { return apiService.put<DetailResponse<CostCategory>>(`/trades/cost-categories/${id}/`, data); }
  deleteCostCategory(id: number)                   { return apiService.delete<DetailResponse<null>>(`/trades/cost-categories/${id}/`); }

  // ── Cost Info ────────────────────────────────────────────────────────────────
  listCosts(params?: { date_from?: string; date_to?: string; cost_category_id?: number; search?: string }) { return apiService.get<ListResponse<CostInfo>>('/trades/costs/', { params }); }
  getCost(id: number) { return apiService.get<DetailResponse<CostInfo>>(`/trades/costs/${id}/`); }
  createCost(data: CostInfoPayload) { return apiService.post<DetailResponse<CostInfo>>('/trades/costs/', data); }
  updateCost(id: number, data: Partial<CostInfoPayload>) { return apiService.put<DetailResponse<CostInfo>>(`/trades/costs/${id}/`, data); }
  deleteCost(id: number) { return apiService.delete<DetailResponse<null>>(`/trades/costs/${id}/`); }

  // ── ROI Info ──────────────────────────────────────────────────────────────────
  listRois(params?: { date_from?: string; date_to?: string; roi_type?: string; search?: string }) {
    return apiService.get<ListResponse<RoiInfo>>('/trades/roi/', { params });
  }
  getRoi(id: number) {
    return apiService.get<DetailResponse<RoiInfo>>(`/trades/roi/${id}/`);
  }
  createRoi(data: RoiInfoPayload) {
    return apiService.post<DetailResponse<RoiInfo>>('/trades/roi/', data);
  }
  updateRoi(id: number, data: Partial<RoiInfoPayload>) {
    return apiService.put<DetailResponse<RoiInfo>>(`/trades/roi/${id}/`, data);
  }
  deleteRoi(id: number) {
    return apiService.delete<DetailResponse<null>>(`/trades/roi/${id}/`);
  }

  // ── Purchases ────────────────────────────────────────────────────────────────
  listPurchases(params?: { date_from?: string; date_to?: string; product_id?: number; search?: string }) { return apiService.get<ListResponse<PurchaseInfo>>('/trades/purchases/', { params }); }
  getPurchase(id: number)                      { return apiService.get<DetailResponse<PurchaseInfo>>(`/trades/purchases/${id}/`); }
  createPurchase(data: PurchaseInfoPayload)    { return apiService.post<DetailResponse<PurchaseInfo>>('/trades/purchases/', data); }
  updatePurchase(id: number, data: Partial<PurchaseInfoPayload>) { return apiService.put<DetailResponse<PurchaseInfo>>(`/trades/purchases/${id}/`, data); }
  deletePurchase(id: number)                   { return apiService.delete<DetailResponse<null>>(`/trades/purchases/${id}/`); }

  // ── Purchase Price List ───────────────────────────────────────────────────────
  listPurchasePrices(params: { product_id: number; with_stock?: boolean }) {
    return apiService.get<ListResponse<PurchasePriceList>>('/trades/purchase-price-list/', { params });
  }

  // ── Sales ────────────────────────────────────────────────────────────────────
  listSales(params?: { date_from?: string; date_to?: string; product_id?: number; seller_info_id?: number; sell_type?: string; search?: string }) { return apiService.get<ListResponse<SellingInfo>>('/trades/sales/', { params }); }
  getSale(id: number)                       { return apiService.get<DetailResponse<SellingInfo>>(`/trades/sales/${id}/`); }
  createSale(data: SellingInfoPayload)      { return apiService.post<DetailResponse<SellingInfo>>('/trades/sales/', data); }
  updateSale(id: number, data: Partial<SellingInfoPayload>) { return apiService.put<DetailResponse<SellingInfo>>(`/trades/sales/${id}/`, data); }
  deleteSale(id: number)                    { return apiService.delete<DetailResponse<null>>(`/trades/sales/${id}/`); }
  createSalesBulk(data: SellingBulkPayload) {
    return apiService.post<{ success: boolean; message: string; data: SellingInfo[] }>('/trades/sales/bulk/', data);
  }
  // ── Returns ──────────────────────────────────────────────────────────────────
  listReturns(params?: { date_from?: string; date_to?: string; product_id?: number; seller_info_id?: number; search?: string }) { return apiService.get<ListResponse<ReturnInfo>>('/trades/returns/', { params }); }
  getReturn(id: number)                        { return apiService.get<DetailResponse<ReturnInfo>>(`/trades/returns/${id}/`); }
  createReturn(data: ReturnInfoPayload)        { return apiService.post<DetailResponse<ReturnInfo>>('/trades/returns/', data); }
  updateReturn(id: number, data: Partial<ReturnInfoPayload>) { return apiService.put<DetailResponse<ReturnInfo>>(`/trades/returns/${id}/`, data); }
  deleteReturn(id: number)                     { return apiService.delete<DetailResponse<null>>(`/trades/returns/${id}/`); }
}
export const tradesService = new TradesService();